import {
  ADD_REAL_TIME_NOTIFICATION,
  BUMP_QUOTE_LIST_REFRESH,
  CLEAR_NOTIFICATIONS,
  MARK_ALL_NOTIFICATIONS_READ,
  MARK_NOTIFICATION_READ,
  SEED_NOTIFICATIONS,
} from "./constants";

const initialNotificationState = {
  realTimeNotifications: [],
  unreadCount: 0,
  quoteListRefreshNonce: 0,
  lastQuoteListEvent: null,
};

export const notificationReducer = (state = initialNotificationState, action) => {
  switch (action.type) {
    case ADD_REAL_TIME_NOTIFICATION: {
      // A reconnecting SSE stream replays recent events
      // (sseManager.replayRecentSseEvents), so the same notification can arrive
      // twice. Ignore a repeat rather than showing it twice and double-counting
      // the unread badge.
      const incomingId = action.payload?.id;
      if (
        incomingId != null &&
        state.realTimeNotifications.some((n) => n.id === incomingId)
      ) {
        return state;
      }

      const newNotification = { ...action.payload, read: false };
      const updatedNotifications = [newNotification, ...state.realTimeNotifications];

      const trimmedNotifications = updatedNotifications.slice(0, 50);

      return {
        ...state,
        realTimeNotifications: trimmedNotifications,
        // Count what the operator can actually open. The list is capped at 50
        // but the counter used to keep climbing past the trim, so the 51st live
        // event printed "50 · 51 new" in one line of the bell panel.
        unreadCount: trimmedNotifications.filter((n) => !n.read).length,
      };
    }

    case SEED_NOTIFICATIONS: {
      // History fetched from GET /notifications. Anything already in the buffer
      // arrived live and is newer, so it wins and keeps its position; history
      // fills in beneath it. Guard a bad payload so a failed/odd response can
      // never blank a bell that already has live items in it.
      if (!Array.isArray(action.payload)) return state;

      const seen = new Set(
        state.realTimeNotifications.map((n) => n.id).filter((id) => id != null)
      );
      const history = action.payload.filter((n) => {
        if (n?.id == null) return true;
        if (seen.has(n.id)) return false;
        seen.add(n.id);
        return true;
      });

      const merged = [...state.realTimeNotifications, ...history].slice(0, 50);

      return {
        ...state,
        realTimeNotifications: merged,
        unreadCount: merged.filter((n) => !n.read).length,
      };
    }

    case MARK_NOTIFICATION_READ: {
      const updatedNotifications = state.realTimeNotifications.map(notification =>
        notification.id === action.payload
          ? { ...notification, read: true }
          : notification
      );

      const unreadCount = updatedNotifications.filter(n => !n.read).length;

      return {
        ...state,
        realTimeNotifications: updatedNotifications,
        unreadCount,
      };
    }

    case MARK_ALL_NOTIFICATIONS_READ: {
      const updatedNotifications = state.realTimeNotifications.map(notification => ({
        ...notification,
        read: true,
      }));

      return {
        ...state,
        realTimeNotifications: updatedNotifications,
        unreadCount: 0,
      };
    }

    case CLEAR_NOTIFICATIONS: {
      return {
        ...state,
        realTimeNotifications: [],
        unreadCount: 0,
      };
    }

    case BUMP_QUOTE_LIST_REFRESH: {
      return {
        ...state,
        quoteListRefreshNonce: (state.quoteListRefreshNonce || 0) + 1,
        lastQuoteListEvent: action.payload || null,
      };
    }

    default:
      return state;
  }
};
